import React, { useState } from "react";
import { Prompt } from "react-router-dom";
import { Search, Trash2 } from "lucide-react";
import { useJsApiLoader } from "@react-google-maps/api";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import { GOOGLE_MAPS_LOADER_ID } from "../../../../lib/googleMapsConfig";
import DetailHeader from "../components/DetailHeader";
import JobLocationMap from "../components/JobLocationMap";
import AuditHistory from "../components/AuditHistory";
import DeleteGeofenceModal from "./DeleteGeofenceModal";
import TextInput from "../../../common/TextInput";
import Validation from "../../../common/Validation";
import { formatAddress } from "./JobLocationsList";
import {
  feetToMeters,
  metersToFeet,
  getPolygonCenter,
  getPolygonRadiusInMeters,
  isPolygonSimple,
} from "../../../../lib/mapUtils";
import "./RoleDetail.css";
import "./JobLocationDetail.css";

const MIN_RADIUS_FEET = 300;
const MAX_RADIUS_FEET = 4000;
const MIN_RADIUS_METERS = 91.44; // matches mobile's minimum enforced radius

function parsePolygonCoords(job) {
  if (!job?.polygon_coords) return [];
  try {
    const coords = JSON.parse(job.polygon_coords);
    if (!Array.isArray(coords)) return [];
    return coords.map(([lat, lng]) => ({ lat, lng }));
  } catch (e) {
    return [];
  }
}

function buildFormData(job) {
  const radiusMeters = job?.radius != null ? Number(job.radius) : null;
  return {
    latitude: job?.latitude != null ? String(job.latitude) : "",
    longitude: job?.longitude != null ? String(job.longitude) : "",
    location_address: formatAddress(job) || "",
    radiusFeet:
      radiusMeters != null
        ? Math.round(metersToFeet(radiusMeters))
        : MIN_RADIUS_FEET,
    polygonPoints: parsePolygonCoords(job),
  };
}

export default function JobLocationDetail({ job, onBack, onUpdate }) {
  const [displayJob, setDisplayJob] = useState(job);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(buildFormData(job));
  const [mode, setMode] = useState(job?.polygon_coords ? "polygon" : "point");
  // Bumped whenever the drawn region should be discarded/reloaded from
  // scratch (start/cancel editing, mode switch, clear) so JobLocationMap
  // remounts its <Polygon> instead of feeding it a new `paths` prop while
  // mounted — see the comment in JobLocationMap's handlePolygonLoad.
  const [polygonResetKey, setPolygonResetKey] = useState(0);
  const [isMovingRegion, setIsMovingRegion] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState(null);
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const hasGeofence =
    displayJob?.latitude != null ||
    displayJob?.longitude != null ||
    !!displayJob?.polygon_coords;

  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const latitude = formData.latitude === "" ? null : Number(formData.latitude);
  const longitude =
    formData.longitude === "" ? null : Number(formData.longitude);

  const handleStartEdit = () => {
    const initial = buildFormData(displayJob);
    setFormData(initial);
    setMode(displayJob?.polygon_coords ? "polygon" : "point");
    setPolygonResetKey((k) => k + 1);
    setIsMovingRegion(false);
    setSaveError(null);
    setGeocodeError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setFormData(buildFormData(displayJob));
    setMode(displayJob?.polygon_coords ? "polygon" : "point");
    setPolygonResetKey((k) => k + 1);
    setIsMovingRegion(false);
    setSaveError(null);
    setGeocodeError(null);
    setIsEditing(false);
  };

  const handleBackClick = () => {
    if (isEditing) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to leave without saving?"
      );
      if (!confirmed) return;
      handleCancelEdit();
    }
    onBack && onBack();
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setSaveError(null);
    setMode(nextMode);
    setPolygonResetKey((k) => k + 1);
    setIsMovingRegion(false);
    if (nextMode === "polygon") {
      setFormData((prev) => ({ ...prev, polygonPoints: [] }));
    } else {
      setFormData((prev) => ({
        ...prev,
        radiusFeet: prev.radiusFeet || MIN_RADIUS_FEET,
      }));
    }
  };

  const handleClearRegion = () => {
    setFormData((prev) => ({ ...prev, polygonPoints: [] }));
    setPolygonResetKey((k) => k + 1);
    setIsMovingRegion(false);
  };

  const handleDeleteGeofence = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    const result = await FieldConfigurationAPI.updateJobLocation(
      displayJob.uuid,
      {
        latitude: null,
        longitude: null,
        location_address: null,
        radius: null,
        polygon_coords: null,
      },
      displayJob.company_uuid
    );

    setIsDeleting(false);

    if (result.status === 200 && result.data) {
      setDisplayJob(result.data);
      onUpdate && onUpdate(result.data);
      setFormData(buildFormData(result.data));
      setMode("point");
      setPolygonResetKey((k) => k + 1);
      setIsMovingRegion(false);
      setIsEditing(false);
      setConfirmDeleteOpen(false);
      setAuditRefreshKey((k) => k + 1);
    } else {
      setDeleteError("Failed to delete location geofence. Please try again.");
    }
  };

  const handleMapChange = ({ latitude: lat, longitude: lng }) => {
    setFormData((prev) => ({
      ...prev,
      latitude: String(lat),
      longitude: String(lng),
    }));
  };

  const handleRadiusChange = (meters) => {
    setFormData((prev) => ({
      ...prev,
      radiusFeet: Math.round(metersToFeet(meters)),
    }));
  };

  const handlePolygonChange = (points) => {
    setFormData((prev) => ({ ...prev, polygonPoints: points }));
  };

  const handleGeocodeAddress = () => {
    const address = formData.location_address.trim();
    if (!address || !window.google?.maps?.Geocoder) return;

    setIsGeocoding(true);
    setGeocodeError(null);

    new window.google.maps.Geocoder().geocode(
      { address },
      (results, status) => {
        setIsGeocoding(false);
        if (status === "OK" && results?.[0]) {
          const location = results[0].geometry.location;
          setFormData((prev) => ({
            ...prev,
            latitude: String(location.lat()),
            longitude: String(location.lng()),
            location_address:
              results[0].formatted_address || prev.location_address,
          }));
        } else {
          setGeocodeError("Couldn't find that address. Try refining it.");
        }
      }
    );
  };

  const reverseGeocode = (point) =>
    new Promise((resolve) => {
      if (!window.google?.maps?.Geocoder) {
        resolve(null);
        return;
      }
      new window.google.maps.Geocoder().geocode(
        { location: point },
        (results, status) => {
          if (status === "OK" && results?.[0]) {
            resolve(results[0].formatted_address || null);
          } else {
            resolve(null);
          }
        }
      );
    });

  const handleSave = async () => {
    setSaveError(null);

    let payload;

    if (mode === "polygon") {
      const points = formData.polygonPoints || [];
      if (points.length < 3) {
        setSaveError("Add at least 3 points to define a region.");
        return;
      }
      if (!isPolygonSimple(points)) {
        setSaveError("This region's boundary crosses itself.");
        return;
      }
      const regionCenter = getPolygonCenter(points);
      const regionRadius = getPolygonRadiusInMeters(points);
      if (regionRadius < MIN_RADIUS_METERS) {
        setSaveError("Region is too small.");
        return;
      }

      setIsSaving(true);
      const geocodedAddress = await reverseGeocode(regionCenter);

      payload = {
        latitude: regionCenter.lat,
        longitude: regionCenter.lng,
        location_address:
          geocodedAddress || formData.location_address.trim() || null,
        radius: regionRadius,
        polygon_coords: JSON.stringify(points.map((p) => [p.lat, p.lng])),
      };
    } else {
      setIsSaving(true);
      payload = {
        latitude,
        longitude,
        location_address: formData.location_address.trim() || null,
        radius: formData.radiusFeet ? feetToMeters(formData.radiusFeet) : null,
        polygon_coords: null,
      };
    }

    const result = await FieldConfigurationAPI.updateJobLocation(
      displayJob.uuid,
      payload,
      displayJob.company_uuid
    );

    setIsSaving(false);

    if (result.status === 200 && result.data) {
      setDisplayJob(result.data);
      onUpdate && onUpdate(result.data);
      setMode(result.data.polygon_coords ? "polygon" : "point");
      setIsMovingRegion(false);
      setIsEditing(false);
      setAuditRefreshKey((k) => k + 1);
    } else {
      setSaveError("Failed to save job location.");
    }
  };

  return (
    <div className="role-detail">
      {/* Catches router-driven navigation (tab menu clicks, browser
          back/forward) that the manual handleBackClick guard can't see,
          since those go through history.push rather than the onBack prop. */}
      <Prompt
        when={isEditing}
        message="You have unsaved changes. Are you sure you want to leave without saving?"
      />
      <DetailHeader
        title={displayJob.description}
        subtitle={<span>{displayJob.code}</span>}
        onBack={handleBackClick}
        onEdit={isEditing ? handleCancelEdit : handleStartEdit}
        onSave={handleSave}
        isEditing={isEditing}
        saveDisabled={isSaving}
        backLabel="Back to Job Locations"
        editLabel="Edit Location"
        saveLabel={isSaving ? "Saving..." : "Save"}
      />

      <div className="role-detail__content">
        {saveError && <Validation>{saveError}</Validation>}

        <div className="role-detail__section role-detail__section--info">
          <div className="job-location-detail__map-header">
            <h3 className="role-detail__section-title">Map</h3>
            {!isEditing && hasGeofence && (
              <button
                type="button"
                className="job-location-detail__search-button"
                onClick={() => {
                  setDeleteError(null);
                  setConfirmDeleteOpen(true);
                }}
              >
                <Trash2 size={14} />
                Delete Geofence
              </button>
            )}
            {isEditing && (
              <div className="job-location-detail__mode-toggle">
                <button
                  type="button"
                  className={
                    "job-location-detail__mode-button" +
                    (mode === "point"
                      ? " job-location-detail__mode-button--active"
                      : "")
                  }
                  onClick={() => handleModeChange("point")}
                >
                  Pin &amp; Radius
                </button>
                <button
                  type="button"
                  className={
                    "job-location-detail__mode-button" +
                    (mode === "polygon"
                      ? " job-location-detail__mode-button--active"
                      : "")
                  }
                  onClick={() => handleModeChange("polygon")}
                >
                  Region
                </button>
              </div>
            )}
          </div>
          <JobLocationMap
            latitude={latitude}
            longitude={longitude}
            editable={isEditing}
            onChange={handleMapChange}
            mode={mode}
            radius={
              mode === "point" && formData.radiusFeet
                ? feetToMeters(formData.radiusFeet)
                : null
            }
            polygonPoints={formData.polygonPoints}
            onRadiusChange={handleRadiusChange}
            onPolygonChange={handlePolygonChange}
            resetKey={polygonResetKey}
            movingRegion={isMovingRegion}
          />
          {isEditing && mode === "point" && latitude != null && (
            <div className="job-location-detail__radius-row">
              <label className="role-detail__label">Radius</label>
              <input
                type="range"
                min={MIN_RADIUS_FEET}
                max={MAX_RADIUS_FEET}
                step={50}
                value={formData.radiusFeet || MIN_RADIUS_FEET}
                onChange={(e) => {
                  const feet = Number(e.target.value);
                  setFormData((prev) => ({ ...prev, radiusFeet: feet }));
                }}
              />
              <span className="job-location-detail__radius-value">
                {formData.radiusFeet || MIN_RADIUS_FEET} ft
              </span>
            </div>
          )}
          {isEditing && mode === "polygon" && (
            <div className="job-location-detail__radius-row">
              <label className="role-detail__label">Approx. radius</label>
              <span className="job-location-detail__radius-value">
                {formData.polygonPoints && formData.polygonPoints.length >= 3
                  ? `${Math.round(
                      metersToFeet(
                        getPolygonRadiusInMeters(formData.polygonPoints)
                      )
                    )} ft`
                  : "—"}
              </span>
            </div>
          )}
          {isEditing && mode === "polygon" && (
            <div className="job-location-detail__region-actions">
              <span className="job-location-detail__region-hint">
                {formData.polygonPoints && formData.polygonPoints.length > 0
                  ? `${formData.polygonPoints.length} point${
                      formData.polygonPoints.length === 1 ? "" : "s"
                    }`
                  : "Click the map to start drawing a region"}
              </span>
              <div className="job-location-detail__region-buttons">
                {formData.polygonPoints &&
                  formData.polygonPoints.length >= 3 && (
                    <button
                      type="button"
                      className={
                        "job-location-detail__search-button" +
                        (isMovingRegion
                          ? " job-location-detail__mode-button--active"
                          : "")
                      }
                      onClick={() => setIsMovingRegion((v) => !v)}
                    >
                      {isMovingRegion ? "Done Moving" : "Move Region"}
                    </button>
                  )}
                {formData.polygonPoints &&
                  formData.polygonPoints.length > 0 && (
                    <button
                      type="button"
                      className="job-location-detail__search-button"
                      onClick={handleClearRegion}
                    >
                      <Trash2 size={14} />
                      Clear Region
                    </button>
                  )}
              </div>
            </div>
          )}
        </div>

        <div className="role-detail__section role-detail__section--info">
          <h3 className="role-detail__section-title">Location Details</h3>
          <div className="role-detail__fields">
            <div className="role-detail__field">
              <label className="role-detail__label">Latitude</label>
              {isEditing ? (
                <TextInput
                  type="number"
                  value={formData.latitude}
                  placeholder="e.g. 36.1263"
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({ ...prev, latitude: value }));
                  }}
                />
              ) : (
                <span className="role-detail__value">
                  {formData.latitude || "—"}
                </span>
              )}
            </div>
            <div className="role-detail__field">
              <label className="role-detail__label">Longitude</label>
              {isEditing ? (
                <TextInput
                  type="number"
                  value={formData.longitude}
                  placeholder="e.g. -86.6774"
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({ ...prev, longitude: value }));
                  }}
                />
              ) : (
                <span className="role-detail__value">
                  {formData.longitude || "—"}
                </span>
              )}
            </div>
            <div className="role-detail__field">
              <label className="role-detail__label">Address</label>
              {isEditing ? (
                <>
                  <div className="job-location-detail__address-row">
                    <TextInput
                      value={formData.location_address}
                      placeholder="e.g. 123 Main St, Nashville, TN"
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          location_address: value,
                        }));
                      }}
                    />
                    <button
                      type="button"
                      className="job-location-detail__search-button"
                      onClick={handleGeocodeAddress}
                      disabled={
                        !isLoaded ||
                        isGeocoding ||
                        !formData.location_address.trim()
                      }
                    >
                      <Search size={14} />
                      {isGeocoding ? "Searching..." : "Search"}
                    </button>
                  </div>
                  {geocodeError && <Validation>{geocodeError}</Validation>}
                </>
              ) : (
                <span className="role-detail__value">
                  {formData.location_address || "—"}
                </span>
              )}
            </div>
          </div>
        </div>

        <AuditHistory
          resource="job_locations"
          service="catalog"
          uuid={displayJob.uuid}
          refreshKey={auditRefreshKey}
          createdAt={displayJob.created_at}
          fieldLabels={{ polygon_coords: "Region" }}
          valueFormatters={{
            polygon_coords: (value) => {
              if (!value) return null;
              try {
                const points = JSON.parse(value);
                return Array.isArray(points) && points.length
                  ? `${points.length}-point region`
                  : null;
              } catch (e) {
                return null;
              }
            },
          }}
        />
      </div>

      <DeleteGeofenceModal
        open={confirmDeleteOpen}
        isDeleting={isDeleting}
        error={deleteError}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteGeofence}
      />
    </div>
  );
}
